import {Injectable} from '@angular/core';
import {ApiClientService} from '../api-client.service';
import {Observable} from 'rxjs/Observable';
import {ISuite, ISuiteSelectOption} from '../../types/api-response/building/suite';
import {ISuiteUser} from '../../types/api-response/building/suite-user';
import {IPet, IPetType} from '../../types/api-response/building/pet';
import {IVehicle} from '../../types/api-response/building/vehicle';
import {ApiResponseList} from '../../types/api-response/helpers';
import {IResidentPatch} from '../../types/api-response/building/resident-patch';
import {IEmergencyContacts} from '../../types/api-response/building/emergency-contacts';
import {IBuilding} from '../../types/api-response/building';
import {catchError, flatMap, map, mergeMap, toArray} from 'rxjs/operators';
import 'rxjs/add/operator/catch';
import 'rxjs/add/observable/throw';
import {UrlService} from '../url.service';
import {ICorporationNumber, ICorporationNumberShort} from '../../types/api-response/building/corporation-number';
import {IS3Credentials} from '../../types/api-response/building/s3-credentials';
import {IFileUpload} from '../../types/api-response/fileUpload';
import {IUserGroup} from '../../types/api-response/building/user-group';
import {ICorporationStripeAccount} from '../../types/api-response/building/corporation-stripe-account';
import {of} from 'rxjs/observable/of';
import {GetBuildingsRequest, GetBuildingsShortRequest, GetUserGroupsRequest} from '../../types/api-request/building';
import {GetSuitesSelectRequest} from '../../types/api-request/building/getSuitesSelect';
import {GetResidentsRequest} from '../../types/api-request/building/getResidents';
import {IResident} from '../../types/api-response/building/resident';
import {ResidentForSelect, SuiteUser} from '../../types/models';
import {GetCorporationSettingsRequest} from '../../types/api-request/building/getCorporationSettings';
import {ICorporationSettings} from '../../types/api-response/corporation';

@Injectable()
export class BuildingApiService {


    constructor(
        private api: ApiClientService,
        private urlService: UrlService
    ) {
    }

    /**
     * Returns buildings details list
     * @param params
     * @param isShort
     */
    public getBuildings(params: GetBuildingsRequest | GetBuildingsShortRequest = {}, isShort = true): Observable<ApiResponseList<IBuilding>> {
        const url = `${isShort ? '/v1/buildings/short.json' : '/v1/buildings.json'}${ApiClientService.serialize(params)}`;
        return this.api.getJson('domainBD', url)
            .pipe(
                catchError(
                    (error) => {
                        this.api.errorHandler(error, 'Failed to get Buildings');
                        throw error;
                    }
                )
            );
    }

    /**
     * Returns suite details by suite ID
     *
     * docs: https://building-demo.mcc-dev.org/api/doc#section-Suite%20section
     *
     * @param params
     */
    public getSuiteList(params: { [key: string]: string | number }): Observable<ApiResponseList<ISuite>> {
        const url = `/v1/suites` + ApiClientService.serialize(params);

        return this.api.getJson('domainBD', url);
    }

    public getSuitesSelect(params: GetSuitesSelectRequest = {}): Observable<ApiResponseList<ISuiteSelectOption>> {
        return this.api.getJson('domainBD', `/v1/suite/select.json${ApiClientService.serialize(params)}`);
    }

    /**
     * Returns corporation number list
     *
     * docs: https://building-demo.mcc-dev.org/api/doc#section-CorporationNumber%20section
     *
     * @param params
     * @param short
     */
    public getCorporationNumberList(params: { [key: string]: string | number } = {}, short = true): Observable<ApiResponseList<ICorporationNumber | ICorporationNumberShort>> {
        const url = '/v1/corporation_number' + (short ? '/short' : '');
        return this.api.getJson('domainBD', url + ApiClientService.serialize(params));
    }

    /**
     * Get list of CorporationNumber chunked
     *
     * docs: https://building-demo.mcc-dev.org/api/doc#get--api-v1-corporation_number-select.{_format}
     *
     * @param params
     */
    public getCorporationNumberChunks(params: { [key: string]: string | number } = {}): Observable<ApiResponseList<ICorporationNumber>> {
        const url = '/v1/corporation_number/short.json';
        return this.api.getJson('domainBD', url + ApiClientService.serialize(params));
    }

    /**
     * Updates corporation number
     *
     * docs: https://building-demo.mcc-dev.org/api/doc#section-CorporationNumber%20section
     *
     * @param corporationNumber
     */
    public updateCorporationNumber(corporationNumber: Partial<ICorporationNumber>): Observable<ICorporationNumber> {
        const url = `/v1/corporation_number/${corporationNumber.id}`;

        const payload = {...corporationNumber};
        delete payload.id;

        return this.api.patch('domainBD', url, payload);
    }

    getCorporationSettings(corporationNumberId: number, params: GetCorporationSettingsRequest = {}): Observable<ICorporationSettings> {
        return (this.api.getJson('domainBD', `/v1/corporation_number/${corporationNumberId}.json${ApiClientService.serialize(params)}`) as Observable<ICorporationNumber>)
            .pipe(
                map(r => r.settings as ICorporationSettings)
            );
    }

    /**
     * Returns suite details by suite ID
     *
     * docs: https://building-demo.mcc-dev.org/api/doc#section-Suite%20section
     *
     * @param suiteId
     * @param expand
     */
    public getSuite(suiteId: number, expand?: string): Observable<ISuite> {
        let url = `/v1/suites/${suiteId}`;

        if (expand) {
            url += `?expand=${expand}`;
        }

        return this.api.get('domainBD', url).map(
            res => res.json(),
            (error) => this.api.errorHandler(error, 'Failed to get Suite')
        );
    }

    /**
     * Sets details for new SuiteUser
     *
     * @param suiteUser
     */
    public createSuiteUser(suiteUser: Partial<ISuiteUser>) {
        return this.api.post('domainBD', `/v1/suite-users.json`, suiteUser)
            .catch(resError => Observable.throw(resError.json()));
    }

    /**
     * Patches SuiteUser
     *
     * docs: https://building-demo.mcc-dev.org/api/doc#section-SuiteUser%20section
     *
     * @param suiteUser
     */
    public updateSuiteUser(suiteUser: Partial<ISuiteUser>) {
        const suiteUserId = suiteUser.id;

        delete suiteUser.id; // force remove property to avoid api error

        return this.api.patch('domainBD', `/v1/suite-users/${suiteUserId}.json`, suiteUser).pipe(
            catchError((error) => {
                this.api.errorHandler(error, 'Failed to update SuiteUser');

                return error;
            })
        );
    }

    /**
     * Returns suite user details by suiteUser ID
     *
     * docs: https://building-demo.mcc-dev.org/api/doc#section-SuiteUser%20section
     *
     * @param suiteUserId
     * @param expand
     */
    public getSuiteUser(suiteUserId: number, expand?: string): Observable<ISuiteUser> {
        let url = `/v1/suite-users/${suiteUserId}`;

        if (expand) {
            url += `?expand=${expand}`;
        }

        return this.api.get('domainBD', url).map(
            res => res.json(),
            (error) => this.api.errorHandler(error, 'Failed to get Suite')
        );
    }

    /**
     * Set SuitUser details
     *
     * docs: https://building-demo.mcc-dev.org/api/doc#section-SuiteUser%20section
     *
     * @param suiteUser
     */
    setSuiteUser(suiteUser: Partial<ISuiteUser>) {
        return suiteUser.id ? this.updateSuiteUser(suiteUser) : this.createSuiteUser(suiteUser);
    }

    /**
     * Sets details for new pet
     *
     * @param pet
     */
    createPet(pet: IPet) {
        const payload = JSON.parse(JSON.stringify(pet));

        delete payload.id; // force remove property to avoid api error

        return this.api.post('domainBD', `/v1/pet.json`, payload);
    }

    /**
     * Patches pet details
     *
     * docs: https://building-demo.mcc-dev.org/api/doc#section-Pet%20section
     *
     * @param pet
     */
    public updatePet(pet: IPet) {
        const payload = JSON.parse(JSON.stringify(pet));
        const petId = payload.id;

        delete payload.id; // force remove property to avoid api error

        return this.api.patch('domainBD', `/v1/pet/${petId}.json`, payload);
    }

    /**
     * Adds new or patch existing pet item
     *
     * docs: https://building-demo.mcc-dev.org/api/doc#section-Pet%20section
     *
     * @param pet
     */
    public setPet(pet: IPet): Observable<any> {
        return pet.id ? this.updatePet(pet) : this.createPet(pet);
    }

    /**
     * Adds new vehicle details
     *
     * docs: https://building-demo.mcc-dev.org/api/doc#section-Vehicle%20section
     *
     * @param body
     */
    createVehicle(body: IVehicle) {
        const payload = JSON.parse(JSON.stringify(body));

        delete payload.id; // force remove property to avoid api error

        return this.api.post('domainBD', `/v1/vehicle.json`, payload);
    }

    /**
     * Patches existing Vehicle details
     *
     * docs: https://building-demo.mcc-dev.org/api/doc#section-Vehicle%20section
     *
     * @param vehicle
     */
    public updateVehicle(vehicle: IVehicle) {
        const vehicleId = vehicle.id;
        const payload = JSON.parse(JSON.stringify(vehicle));

        delete payload.id; // force remove property to avoid api error

        return this.api.patch('domainBD', `/v1/vehicle/${vehicleId}.json`, payload);
    }

    /**
     * Sets Vehicle details
     *
     * docs: https://building-demo.mcc-dev.org/api/doc#section-Vehicle%20section
     *
     * @param vehicle
     */
    setVehicle(vehicle: IVehicle) {
        return vehicle.id ? this.updateVehicle(vehicle) : this.createVehicle(vehicle);
    }

    /**
     * Searches for vehicles by license plate
     * and returns vehicles list as result
     *
     * docs: https://building-demo.mcc-dev.org/api/doc#section-Vehicle%20section
     *
     * @param licencePlate
     */
    public findVehicleByLicencePlate(licencePlate: string): Observable<IVehicle> {
        return this.api.get('domainBD', `/v1/vehicle.json?query=:licensePlateForSearch|licensePlate:${licencePlate}&show_moved_out=true`).map(
            res => res.json().entities.shift() || null,
            (error) => this.api.errorHandler(error, 'Failed to get Suite')
        );
    }

    /**
     * Patches vehicle details
     * or create if not exist
     *
     * docs: https://building-demo.mcc-dev.org/api/doc#section-Vehicle%20section
     *
     * @param vehicle
     */
    public checkAndSetVehicle(vehicle: IVehicle) {
        if (!vehicle.licensePlate) {
            return this.setVehicle(vehicle);
        }
        const licencePlateForSearch = String(vehicle.licensePlate).replace(/[^a-zA-Z0-9]/g, '');
        return this.findVehicleByLicencePlate(licencePlateForSearch).pipe(
            mergeMap(foundVehicle => {
                if (foundVehicle) {
                    vehicle.id = foundVehicle.id;
                    vehicle.moveOutDate = null;
                }

                return this.setVehicle(vehicle);
            })
        );
    }

    /**
     * Returns suite details by suite ID
     *
     * docs: https://building-demo.mcc-dev.org/api/doc#section-ResidentPatch%20section
     *
     * @param patchId
     * @param expand
     */
    public getResidentPatch(patchId: string, expand?: string): Observable<IResidentPatch> {
        let url = `/v1/resident_patch/${patchId}`;

        if (expand) {
            url += `?expand=${expand}`;
        }

        return this.api.get('domainBD', url).pipe(
            map(res => res.json()),
            map((patch: IResidentPatch) => { // TODO: remove after bug fixes
                if (patch.userProfile) {
                    if (Array.isArray(patch.userProfile.emergencyContacts)) { // todo: suite ought be a number not an object
                        patch.userProfile.emergencyContacts.map((ecItem: IEmergencyContacts) => {
                            ecItem.suite = ecItem.suite && (ecItem.suite as ISuite).id || ecItem.suite;
                        });
                    }
                }

                return patch;
            })
        );
    }

    /**
     * Returns resident patch details
     *
     * @param params
     * @param includeProcessed
     */
    public getResidentPatchList(params: { [key: string]: string | number }, includeProcessed = false): Observable<ApiResponseList<IResidentPatch>> {
        let url = `/v1/resident_patch.json`;

        if (!includeProcessed) {
            params['processedAt'] = 'is_null_value';
        }

        const paramsArray: string[] = Object.keys(params).map(keyName => `${keyName}=${params[keyName]}`);
        url += paramsArray.length ? '?' + paramsArray.join('&') : '';

        return this.api.getJson('domainBD', url);
    }

    /**
     * Returns resident patches list
     *
     * @param expand
     */
    public getAllResidentPatches(expand?: string): Observable<ApiResponseList<IResidentPatch>> {

        const get = '/v1/resident_patch.json?processedAt=is_null_value&expand=suite.building,suiteUser.user';
        const queryParams = this.urlService.getQueryParamsString();
        const url = `${get}${queryParams}`;

        return this.api.getInfiniteList('domainBD', url);
    }

    /**
     * Marks resident patch as processed
     *
     * @param patchId
     */
    public processResidentPatch(patchId: string): Observable<any> {
        return this.api.post('domainBD', `/v1/resident_patch/${patchId}/process.json`, null);
    }

    /**
     * Returns pet types list
     *
     * docs: https://building-demo.mcc-dev.org/api/doc#section-Pet%20Type%20section
     */
    public getPetTypes(): Observable<ApiResponseList<IPetType>> {
        return this.api.getInfiniteList('domainBD', `/v1/pet_type.json`);
    }

    /**
     * Return user groups list
     *
     * docs: https://building-demo.mcc-dev.org/api/doc#get--api-v1-group.{_format}
     */
    public getAllUserGroups(): Observable<ApiResponseList<IUserGroup>> {
        return this.api.getInfiniteList('domainBD', '/v1/group.json');
    }

    /**
     * Return user groups list
     *
     * docs: https://building-demo.mcc-dev.org/api/doc#get--api-v1-group.{_format}
     */
    public getUserGroups(params: GetUserGroupsRequest): Observable<ApiResponseList<IUserGroup>> {
        return this.api.getJson('domainBD', `/v1/group.json${ApiClientService.serialize(params)}`);
    }

    /**
     * Returns stripe accounts list
     *
     * docs: https://building-demo.mcc-dev.org/api/doc#get--api-v1-stripe_account.{_format}
     *
     * @param params
     */
    public getStripeAccounts(params = {}): Observable<ApiResponseList<ICorporationStripeAccount>> {

        return this.api.getJson('domainBD', '/v1/stripe_account.json' + ApiClientService.serialize(params));
    }

    /**
     * Returns assess credentials
     * for file direct upload to amazon S3 services
     * @param folderName
     */
    public getS3token(folderName): Observable<IS3Credentials> {
        return this.api.post('domainBD', `/v1/upload_s3/${folderName}`, null);
    }

    /**
     * Upload file to amazon
     * using service as proxy
     *
     * @param file
     */
    public fileUpload(file: File): Observable<IFileUpload> {
        const formData: FormData = new FormData();
        formData.append('fileUpload', file, file.name);

        return this.api.post('domainBD', `/_uploader/attachments/upload`, formData);
    }

    /**
     * Returns logout URL
     */
    public getLogoutUrl(): Observable<{ logoutUrl: string }> {
        return this.api.getJson('domainBD', '/v1/logout_url').pipe(
            catchError(() => of({logoutUrl: ''})) // Must always return valid values
        );
    }

    public getAllOccupancyTypes(domain: string, url: string): Observable<any> {
        return this.api.getJson(domain, url).map((response: Response) => {
            return Observable.of(response);
        });
    }

    public getResidents(params: GetResidentsRequest): Observable<ApiResponseList<IResident>> {
        return this.api.getJson('domainBD', `/v1/residents.json${ApiClientService.serialize(params)}`);
    }

    public getResidentsForSelect(params: GetResidentsRequest = {}): Observable<Array<ResidentForSelect>> {
        if (!params.expand) {
            params.expand = 'suiteUser';
        }
        return this.getResidents(params)
            .pipe(
                map(response => response.entities),
                map(residents => {
                    const mappedResidents = residents.map(ResidentForSelect.fromResidentInterface);
                    mappedResidents.push(new ResidentForSelect(0, 'Other'));
                    return mappedResidents;
                })
            );
    }
}



// WEBPACK FOOTER //
// /app/src/app/services/api/building-api.service.ts